import { useState, useEffect } from 'react'
import { X, Send, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSend: (subject: string, body: string) => void
  isSending: boolean
  defaultSubject: string
  defaultBody: string
  recipientEmail: string
  title: string
}

export function EmailPreviewModal({
  isOpen, onClose, onSend, isSending,
  defaultSubject, defaultBody, recipientEmail, title,
}: Props) {
  const [subject, setSubject] = useState(defaultSubject)
  const [body, setBody] = useState(defaultBody)

  useEffect(() => {
    if (isOpen) {
      setSubject(defaultSubject)
      setBody(defaultBody)
    }
  }, [isOpen, defaultSubject, defaultBody])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Mail size={18} className="text-indigo-500" />
            <h2 className="font-semibold text-gray-900">{title}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-200">
            <span className="text-gray-400 text-xs font-medium uppercase tracking-wide w-12 shrink-0">To</span>
            <span className="font-medium text-gray-800">{recipientEmail}</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Message</label>
            <textarea
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={10}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <Button variant="outline" onClick={onClose} disabled={isSending}>Cancel</Button>
          <Button
            onClick={() => onSend(subject, body)}
            disabled={isSending || !subject.trim() || !body.trim()}
            className="gap-1.5"
          >
            <Send size={15} />
            {isSending ? 'Sending…' : 'Send email'}
          </Button>
        </div>
      </div>
    </div>
  )
}
